import axios, { AxiosRequestConfig, HttpStatusCode } from 'axios'

import { AuthService } from '@/api/models/auth'
import {
  REFRESH_TOKEN_RETRY_DELAYS_MS,
  USER_DISABLED_ERROR
} from '@/api/models/auth/lib/auth.constants'
import { BEARER } from '@/api/models/auth/lib/auth.service'
import { INVALID_GRANT_ERROR_CODE } from '@/app/constants/errors'
import {
  RefreshSessionStatus,
  useServiceErrorsStore
} from '@/components/entities/common/model/service-errors.store'

type AuthAxiosRequestConfig = AxiosRequestConfig & {
  alreadyRetriedAfterRefresh?: boolean
}

type FailedRequest = {
  config: AuthAxiosRequestConfig
  resolve: (value: unknown) => void
  reject: (error?: unknown) => void
}

type RetryRequestsQueueProps = {
  token?: string
  error?: unknown
  skipUserInfo?: boolean
}

let refreshTokenPromise: Promise<string> | null = null
let requestsQueue: FailedRequest[] = []

let resolveOnRefreshClick: (() => void) | null = null

const setRefreshSessionStatus = (status: RefreshSessionStatus) => {
  useServiceErrorsStore.getState().setRefreshSessionStatus(status)
}

export const refreshSession = () => {
  setRefreshSessionStatus(RefreshSessionStatus.Loading)
  resolveOnRefreshClick?.()
  resolveOnRefreshClick = null
}

const refetchRequest = (config: AuthAxiosRequestConfig, token?: string) => {
  if (config.headers) {
    config.headers.Authorization = BEARER + token
  }
  config.alreadyRetriedAfterRefresh = true
  return axios.request(config)
}

const isUserInfoRequest = (config: AxiosRequestConfig): boolean => {
  return config.url?.includes('/userInfo') ?? false
}

const retryRequestsQueue = ({
  token,
  error,
  skipUserInfo = false
}: RetryRequestsQueueProps) => {
  requestsQueue.forEach(({ config, resolve, reject }) => {
    if (skipUserInfo && isUserInfoRequest(config)) {
      return
    }

    if (token) {
      resolve(refetchRequest(config, token))
    } else {
      reject(error)
    }
  })

  requestsQueue = []
}

const isInvalidGrantError = (error: unknown) => {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === HttpStatusCode.BadRequest &&
    error.response?.data?.error === INVALID_GRANT_ERROR_CODE
  )
}

const isUserDisabledError = (error: unknown) => {
  return (
    axios.isAxiosError(error) &&
    isInvalidGrantError(error) &&
    error.response?.data?.error_description === USER_DISABLED_ERROR
  )
}

const isRetryableRefreshError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return true
  }

  const status = error.response?.status

  if (
    status === HttpStatusCode.Unauthorized ||
    status === HttpStatusCode.ServiceUnavailable ||
    isInvalidGrantError(error)
  ) {
    return false
  }

  return true
}

const handleRefreshFailure = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return
  }

  const status = error.response?.status

  if (isUserDisabledError(error)) {
    AuthService.logout('/auth/disabled')
    return
  }

  if (status === HttpStatusCode.Unauthorized || isInvalidGrantError(error)) {
    AuthService.logout()
  }
}

const refreshAccessToken = async (): Promise<string> => {
  for (
    let attempt = 0;
    attempt <= REFRESH_TOKEN_RETRY_DELAYS_MS.length;
    attempt++
  ) {
    if (attempt > 0) {
      setRefreshSessionStatus(RefreshSessionStatus.Loading)
      await new Promise(resolve =>
        setTimeout(resolve, REFRESH_TOKEN_RETRY_DELAYS_MS[attempt - 1])
      )
    }

    try {
      const response = await AuthService.refreshToken()
      if (!response?.access_token) {
        throw new Error('No access token from refreshToken')
      }

      await AuthService.getCurrentUser()
      setRefreshSessionStatus(RefreshSessionStatus.None)
      return response.access_token
    } catch (error) {
      if (!isRetryableRefreshError(error)) {
        setRefreshSessionStatus(RefreshSessionStatus.None)
        handleRefreshFailure(error)
        throw error
      }

      if (attempt === 0) {
        setRefreshSessionStatus(RefreshSessionStatus.Loading)
      }
    }
  }

  setRefreshSessionStatus(RefreshSessionStatus.Error)
  await new Promise<void>(resolve => {
    resolveOnRefreshClick = resolve
  })

  return refreshAccessToken()
}

export const refreshTokenStrategy = async (config: AuthAxiosRequestConfig) => {
  if (config.alreadyRetriedAfterRefresh) {
    throw new Error('Request already retried after token refresh')
  }

  if (refreshTokenPromise) {
    return new Promise((resolve, reject) => {
      requestsQueue.push({ config, resolve, reject })
    })
  }

  const { accessToken } = AuthService.getTokens()
  const authorization = config.headers?.Authorization
  const invalidToken =
    typeof authorization === 'string'
      ? authorization.slice(BEARER.length)
      : undefined

  if (accessToken && invalidToken !== accessToken) {
    return refetchRequest(config, accessToken)
  }

  refreshTokenPromise = refreshAccessToken()
    .then(token => {
      retryRequestsQueue({ token, skipUserInfo: true })
      return token
    })
    .catch(error => {
      retryRequestsQueue({ error, skipUserInfo: true })
      throw error
    })
    .finally(() => {
      refreshTokenPromise = null
    })

  const newToken = await refreshTokenPromise
  return refetchRequest(config, newToken)
}
